import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { fetchQuizAnalytics } from '@actions/quizTaking';
import {
  getAllQuestionsBySection,
  getCurrentSessionUID,
  getQuizDetails,
  getQuizSessionAnalytics
} from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { QUESTION_TYPE_MAP } from '@constants/quiz';
import { getCorrectOption } from '@cont/Quiz/Solutions/utils';
import { roundToPrecision } from '@utils/roundNumber';
import isEmpty from 'lodash/isEmpty';
import useNtaSolutionQuestions from './useNtaSolutionQuestions';

const formatSelectedOptions = ({
  type,
  userAttemptList = [],
  answers = []
}) => {
  if (!userAttemptList?.length) {
    return 'N/A';
  }

  if (type === QUESTION_TYPE_MAP.INTEGER) {
    return userAttemptList.map((value) => String(value)).join(', ');
  }

  const ranks = userAttemptList
    .map((uid) => answers.find((answer) => answer.uid === uid)?.rank)
    .filter((rank) => rank != null);

  return ranks.length ? ranks.join(', ') : 'N/A';
};

const formatCorrectOption = (question) => {
  const {
    type,
    answers = [],
    correct_answers_list: correctUids = []
  } = question;

  if (type === QUESTION_TYPE_MAP.INTEGER) {
    const value = getCorrectOption(correctUids);
    return value != null && value !== '' ? String(value) : 'N/A';
  }

  if (!correctUids?.length) {
    return 'N/A';
  }

  const ranks = correctUids
    .map((uid) => answers.find((answer) => answer.uid === uid)?.rank)
    .filter((rank) => rank != null);

  return ranks.length ? ranks.join(', ') : 'N/A';
};

const getQuestionStatusFromSolution = (question) => {
  if (!question.user_attempt_list?.length) {
    return { label: 'N/A', type: 'na' };
  }

  if (question.is_correct_attempt) {
    return { label: 'CORRECT', type: 'correct' };
  }

  return { label: 'WRONG', type: 'wrong' };
};

const buildQuestionRow = ({
  questionUID,
  questionNumber,
  takingQuestion,
  solutionQuestion
}) => {
  if (!solutionQuestion) {
    return {
      key: questionUID,
      label: `Question ${questionNumber}:`,
      selectedOptions: 'N/A',
      correctOption: 'N/A',
      status: { label: 'N/A', type: 'na' },
      isPending: true
    };
  }

  const answers = solutionQuestion.answers ?? takingQuestion?.answers ?? [];

  return {
    key: questionUID,
    label: `Question ${questionNumber}:`,
    selectedOptions: formatSelectedOptions({
      type: solutionQuestion.type,
      userAttemptList: solutionQuestion.user_attempt_list,
      answers
    }),
    correctOption: formatCorrectOption({
      ...takingQuestion,
      ...solutionQuestion,
      answers
    }),
    status: getQuestionStatusFromSolution(solutionQuestion),
    isPending: false
  };
};

const buildQuestionRows = ({ sections, questionsBySection, solutionByUid }) => {
  let questionNumber = 0;
  const questionRows = [];

  sections.forEach((section) => {
    const sectionQuestions = questionsBySection?.[section.uid];

    sectionQuestions?.allIds?.forEach((questionUID) => {
      questionNumber += 1;
      const takingQuestion = sectionQuestions.byId?.[questionUID] ?? {};

      questionRows.push(
        buildQuestionRow({
          questionUID,
          questionNumber,
          takingQuestion,
          solutionQuestion: solutionByUid[questionUID]
        })
      );
    });
  });

  return questionRows;
};

const useNtaResultData = ({ enabled = true } = {}) => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { quizUID } = query;

  const sessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const questionsBySection = useSelectorWithShallowEqual(
    getAllQuestionsBySection
  );
  const analytics = useSelectorWithShallowEqual(
    getQuizSessionAnalytics(sessionUID)
  );

  const {
    solutionByUid,
    loadMoreSolutions,
    hasMoreSolutions,
    isFetchingSolutions
  } = useNtaSolutionQuestions({ enabled });

  useEffect(() => {
    if (!enabled || !quizUID || !sessionUID || !isEmpty(analytics)) {
      return;
    }

    dispatch(fetchQuizAnalytics(quizUID, sessionUID));
  }, [analytics, dispatch, enabled, quizUID, sessionUID]);

  return useMemo(() => {
    const sections = quizDetails?.sections ?? [];
    const userDetails = analytics?.user_details ?? {};
    const positiveScore = userDetails.session_positive_score_sum ?? 0;
    const negativeScore = userDetails.session_negative_score_sum ?? 0;
    const score = roundToPrecision((positiveScore - negativeScore) * 100) / 100;

    const correctCount = userDetails.session_correct_count ?? 0;
    const incorrectCount = userDetails.session_incorrect_count ?? 0;
    const attemptedCount = correctCount + incorrectCount;

    let totalQuestions = 0;
    sections.forEach((section) => {
      totalQuestions += questionsBySection?.[section.uid]?.allIds?.length ?? 0;
    });

    if (!totalQuestions && quizDetails?.total_questions) {
      totalQuestions = quizDetails.total_questions;
    }

    const questionRows = buildQuestionRows({
      sections,
      questionsBySection,
      solutionByUid
    });

    return {
      score,
      totalQuestions,
      totalAttempted: attemptedCount,
      correctAnswers: correctCount,
      incorrectAnswers: incorrectCount,
      questionRows,
      loadMoreSolutions,
      hasMoreSolutions,
      isFetchingSolutions
    };
  }, [
    analytics,
    hasMoreSolutions,
    isFetchingSolutions,
    loadMoreSolutions,
    quizDetails,
    questionsBySection,
    solutionByUid
  ]);
};

export default useNtaResultData;
